import { useState } from "react";
import dayjs from "dayjs";
import { useParams, useOutletContext } from "react-router-dom";

import { Modal } from "@components/Page";
import { MODAL_LARGE_WIDTH, MODAL_LARGE_HEIGHT } from "@components/Constants";
import type { CreateScheduleInputMutation, UpdateScheduleInputMutation } from "@api/types";

import DistributionsScheduleForm, { getNextDate } from "./DistributionsScheduleForm";
import type { Investment, OutletContext, ScheduleFormValues, Schedule } from "./types";

const LABEL_TITLE = "Distributions Schedule";

interface ModalHandle {
  current: {
    open: () => void;
    close: () => void;
  };
}

type Props = {
  modal: ModalHandle;
  schedule?: Schedule;
  investments: Investment[];
  createSchedule: (mutation: CreateScheduleInputMutation) => Promise<unknown>;
  updateSchedule: (scheduleId: string, mutation: UpdateScheduleInputMutation) => Promise<unknown>;
  deleteSchedule: (scheduleId: string) => Promise<unknown>;
}


const DistributionsScheduleModal = ({
  schedule = undefined,
  modal,
  investments,
  createSchedule,
  updateSchedule,
  deleteSchedule,
}: Props) => {
  const [ isOpen, setIsOpen ] = useState(false);

  const onClose = () =>
    setIsOpen(false);

  modal.current.open = () =>
    setIsOpen(true);

  modal.current.close = () =>
    onClose();

  const { id: projectId } = useParams();
  const { project } = useOutletContext<OutletContext>();
  const { name } = project;

  const initialValues: ScheduleFormValues = schedule
    ? {
        name: schedule.name,
        startDate: schedule.startDate,
        nextDate: schedule.nextDate,
        shouldRoundCents: schedule.shouldRoundCents,
        distributionFrequency: schedule.distributionFrequency,
        transactionType: "INVESTMENT_RETURN",
        projectId: schedule.projectId,
        distributions: schedule.distributions,
      }
    : {
        name,
        nextDate: getNextDate(dayjs().toISOString(), "MONTHLY"),
        startDate: dayjs().toISOString(),
        shouldRoundCents: false,
        distributionFrequency: "MONTHLY",
        transactionType: "INVESTMENT_RETURN",
        projectId,
      };

  const modalProps = {
    title: LABEL_TITLE,
    width: MODAL_LARGE_WIDTH,
    destroyOnClose: true,
    isOpen,
    onClose,
  };

  const formProps = {
    scheduleId: schedule?.id,
    onClose,
    investments,
    initialValues,
    createSchedule,
    updateSchedule,
    deleteSchedule,
  };

  return (
    <Modal key={projectId} {...modalProps}>
      <div style={{ minHeight: MODAL_LARGE_HEIGHT }}>
        <DistributionsScheduleForm {...formProps} />
      </div>
    </Modal>
  );
};

export default DistributionsScheduleModal;
